import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../services/api';
import Header from '../components/Header';
import './Administration.css';

function Administration() {
  const [activeSection, setActiveSection] = useState('invoices');
  const [addresses, setAddresses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [adminSearch, setAdminSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', phone: '', mobile: '', role: 'user', password: '' });
  const [userFormError, setUserFormError] = useState('');
  const [userFormSaving, setUserFormSaving] = useState(false);
  const { token, user: currentUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setLoading(true);
    if (activeSection === 'invoices') {
      fetchInvoices();
    } else if (activeSection === 'addresses') {
      fetchAddresses();
    } else if (activeSection === 'templates') {
      fetchTemplates();
    } else if (activeSection === 'users') {
      fetchContacts();
    }
  }, [token, activeSection]);

  const fetchAddresses = async () => {
    try {
      const data = await api.getAddresses();
      setAddresses(data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await api.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const data = await api.getInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const data = await api.getContacts();
      setContacts(data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne skabelon?')) {
      return;
    }
    try {
      await api.deleteTemplate(templateId);
      setTemplates(templates.filter(t => t.id !== templateId));
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(error.message);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', phone: '', mobile: '', role: 'user', password: '' });
    setUserFormError('');
    setShowUserForm(true);
  };

  const handleEditUser = (contact) => {
    setEditingUser(contact);
    setUserForm({
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      role: contact.role || 'user',
      password: '',
    });
    setUserFormError('');
    setShowUserForm(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      setUserFormError('Navn og email er påkrævet');
      return;
    }
    if (!editingUser && !userForm.password) {
      setUserFormError('Adgangskode er påkrævet for nye brugere');
      return;
    }

    setUserFormSaving(true);
    setUserFormError('');
    try {
      const payload = {
        name: userForm.name,
        email: userForm.email,
        phone: userForm.phone || null,
        mobile: userForm.mobile || null,
        role: userForm.role,
      };
      if (userForm.password) {
        payload.password = userForm.password;
      }

      if (editingUser) {
        await api.updateContact(editingUser.id, payload);
      } else {
        await api.createContact(payload);
      }

      setShowUserForm(false);
      setEditingUser(null);
      fetchContacts();
    } catch (error) {
      setUserFormError(error.message || 'Der opstod en fejl');
    } finally {
      setUserFormSaving(false);
    }
  };

  const handleDeleteUser = async (contact) => {
    if (!window.confirm(t('confirmDeleteUser'))) return;
    try {
      await api.deleteContact(contact.id);
      setContacts(contacts.filter(c => c.id !== contact.id));
    } catch (error) {
      alert(error.message);
    }
  };

  const isAdmin = currentUser?.role === 'admin';
  const isViewer = currentUser?.role === 'viewer';
  const canEdit = !isViewer;

  const getAddressName = (addressId) => {
    const addr = addresses.find(a => a.id === addressId);
    return addr ? addr.name : '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(amount);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return t('never');
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const q = adminSearch.toLowerCase();

  const filteredInvoices = invoices.filter((inv) =>
    !q || (inv.invoice_number || '').toLowerCase().includes(q)
    || (inv.customer_reference || '').toLowerCase().includes(q)
    || (inv.status || '').toLowerCase().includes(q)
  );

  const filteredAddresses = addresses.filter((a) =>
    !q || (a.name || '').toLowerCase().includes(q)
    || (a.address || '').toLowerCase().includes(q)
    || (a.city || '').toLowerCase().includes(q)
    || (a.postal_code || '').toLowerCase().includes(q)
  );

  const filteredTemplates = templates.filter((tmpl) =>
    !q || (tmpl.name || '').toLowerCase().includes(q)
  );

  const filteredContacts = contacts.filter((c) =>
    !q || (c.name || '').toLowerCase().includes(q)
    || (c.email || '').toLowerCase().includes(q)
    || (c.phone || '').toLowerCase().includes(q)
    || (c.mobile || '').toLowerCase().includes(q)
  );

  return (
    <div className="dashboard">
      <Header />

      <div className="dashboard-nav">
        <div className="nav-tabs">
          <button
            className={`nav-tab ${location.pathname === '/' ? 'active' : ''}`}
            onClick={() => navigate('/')}
          >
            {t('overview')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/orders' ? 'active' : ''}`}
            onClick={() => navigate('/orders')}
          >
            {t('orders')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/calendar' ? 'active' : ''}`}
            onClick={() => navigate('/calendar')}
          >
            {t('calendar')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/insights' ? 'active' : ''}`}
            onClick={() => navigate('/insights')}
          >
            {t('insights')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/administration' ? 'active' : ''}`}
            onClick={() => navigate('/administration')}
          >
            {t('administration')}
          </button>
        </div>

        <div className="nav-right">
          <div className="search-box">
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={adminSearch}
              onChange={(e) => setAdminSearch(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>

          <button className="btn-book-order" onClick={() => navigate('/book')}>
            {t('bookOrder')}
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-sidebar">
          <button
            className={`admin-menu-item ${activeSection === 'invoices' ? 'active' : ''}`}
            onClick={() => { setActiveSection('invoices'); setAdminSearch(''); }}
          >
            {t('invoices')}
          </button>
          <button
            className={`admin-menu-item ${activeSection === 'addresses' ? 'active' : ''}`}
            onClick={() => { setActiveSection('addresses'); setAdminSearch(''); }}
          >
            {t('addresses')}
          </button>
          <button
            className={`admin-menu-item ${activeSection === 'templates' ? 'active' : ''}`}
            onClick={() => { setActiveSection('templates'); setAdminSearch(''); }}
          >
            {t('templates')}
          </button>
          <button
            className={`admin-menu-item ${activeSection === 'users' ? 'active' : ''}`}
            onClick={() => { setActiveSection('users'); setAdminSearch(''); }}
          >
            {t('users')}
          </button>
        </div>

        <div className="admin-main">
          {/* ── FAKTURAER ── */}
          {activeSection === 'invoices' && (
            <>
              <div className="admin-header">
                <h2 className="page-title">{t('invoices')}</h2>
                <input
                  className="admin-search-input"
                  type="text"
                  placeholder="Søg faktura..."
                  value={adminSearch}
                  onChange={(e) => setAdminSearch(e.target.value)}
                />
              </div>

              {loading ? (
                <p className="loading-text">{t('loading')}</p>
              ) : filteredInvoices.length === 0 ? (
                <div className="empty-state">
                  <p className="empty-text">{t('noInvoices')}</p>
                </div>
              ) : (
                <div className="invoice-table-wrap">
                  <table className="invoice-table">
                    <thead>
                      <tr>
                        <th>{t('invoiceNumber')}</th>
                        <th>{t('date')}</th>
                        <th>{t('dueDate')}</th>
                        <th>{t('orderReference')}</th>
                        <th className="text-right">{t('amount')}</th>
                        <th>{t('orderStatus')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((inv) => (
                        <tr key={inv.id}>
                          <td className="invoice-number">
                            {inv.invoice_number}
                            {inv.type === 'credit_note' && (
                              <span className="badge-credit">{t('creditNote')}</span>
                            )}
                          </td>
                          <td>{formatDate(inv.invoice_date)}</td>
                          <td>{formatDate(inv.due_date)}</td>
                          <td>{inv.customer_reference || '-'}</td>
                          <td className="text-right amount-cell">{formatAmount(inv.total_amount)}</td>
                          <td>
                            <span className={`invoice-status invoice-status--${inv.status}`}>
                              {inv.status === 'paid' ? t('paid') : t('sent')}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-download"
                              onClick={() => api.downloadInvoicePdf(inv.id)}
                            >
                              PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ── ADRESSER (read-only) ── */}
          {activeSection === 'addresses' && (
            <>
              <div className="admin-header">
                <h2 className="page-title">{t('addresses')}</h2>
                <input
                  className="admin-search-input"
                  type="text"
                  placeholder="Søg adresse..."
                  value={adminSearch}
                  onChange={(e) => setAdminSearch(e.target.value)}
                />
              </div>

              {loading ? (
                <p className="loading-text">{t('loading')}</p>
              ) : (
                <div className="addresses-list">
                  {filteredAddresses.map((address) => (
                    <div key={address.id} className="address-card">
                      <div className="address-info">
                        <h3 className="address-name">{address.name}</h3>
                        <p className="address-details">
                          {address.address}, {address.postal_code} {address.city}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── SKABELONER ── */}
          {activeSection === 'templates' && (
            <>
              <div className="admin-header">
                <h2 className="page-title">{t('templates')}</h2>
                <input
                  className="admin-search-input"
                  type="text"
                  placeholder="Søg skabelon..."
                  value={adminSearch}
                  onChange={(e) => setAdminSearch(e.target.value)}
                />
              </div>

              {loading ? (
                <p className="loading-text">{t('loading')}</p>
              ) : filteredTemplates.length === 0 ? (
                <div className="empty-state">
                  <p className="empty-text">Ingen skabeloner endnu</p>
                  <p className="empty-hint">Opret en skabelon fra booking-formularen via "Gem som skabelon"</p>
                </div>
              ) : (
                <div className="templates-list">
                  {filteredTemplates.map((tmpl) => (
                    <div key={tmpl.id} className="template-card">
                      <div className="template-info">
                        <h3 className="template-name">{tmpl.name}</h3>
                        <p className="template-details">
                          {tmpl.pickup_address_id ? getAddressName(tmpl.pickup_address_id) : '-'}
                          {' → '}
                          {tmpl.delivery_address_id ? getAddressName(tmpl.delivery_address_id) : '-'}
                        </p>
                        {tmpl.pickup_time && (
                          <p className="template-meta">Kl. {tmpl.pickup_time}</p>
                        )}
                      </div>
                      <div className="template-actions">
                        <button
                          className="btn-use-template"
                          onClick={() => navigate('/book')}
                        >
                          Brug
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteTemplate(tmpl.id)}
                        >
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── BRUGERE ── */}
          {activeSection === 'users' && (
            <>
              <div className="admin-header">
                <h2 className="page-title">{t('users')}</h2>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    className="admin-search-input"
                    type="text"
                    placeholder="Søg bruger..."
                    value={adminSearch}
                    onChange={(e) => setAdminSearch(e.target.value)}
                  />
                  {isAdmin && (
                    <button className="btn-use-template" onClick={handleAddUser}>
                      + {t('addUser')}
                    </button>
                  )}
                </div>
              </div>

              {showUserForm && (
                <div className="user-form-card">
                  <h3 className="user-form-title">{editingUser ? t('editUser') : t('addUser')}</h3>
                  <div className="user-form-grid">
                    <div className="user-form-field">
                      <label>{t('name')} *</label>
                      <input
                        type="text"
                        value={userForm.name}
                        onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                        placeholder={t('name')}
                      />
                    </div>
                    <div className="user-form-field">
                      <label>{t('email')} *</label>
                      <input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        placeholder={t('email')}
                      />
                    </div>
                    <div className="user-form-field">
                      <label>{t('phone')}</label>
                      <input
                        type="tel"
                        value={userForm.phone}
                        onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                        placeholder={t('phone')}
                      />
                    </div>
                    <div className="user-form-field">
                      <label>{t('mobile')}</label>
                      <input
                        type="tel"
                        value={userForm.mobile}
                        onChange={(e) => setUserForm({ ...userForm, mobile: e.target.value })}
                        placeholder={t('mobile')}
                      />
                    </div>
                    <div className="user-form-field">
                      <label>{t('role')}</label>
                      <select
                        value={userForm.role}
                        onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                      >
                        <option value="user">{t('user')}</option>
                        <option value="viewer">{t('viewer')}</option>
                        <option value="admin">{t('admin')}</option>
                      </select>
                    </div>
                    <div className="user-form-field">
                      <label>{t('newPassword')}{!editingUser ? ' *' : ''}</label>
                      <input
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        placeholder={editingUser ? t('passwordHint') : t('newPassword')}
                      />
                    </div>
                  </div>
                  {userFormError && <p className="user-form-error">{userFormError}</p>}
                  <div className="user-form-actions">
                    <button
                      className="btn-use-template"
                      onClick={handleSaveUser}
                      disabled={userFormSaving}
                    >
                      {userFormSaving ? (editingUser ? t('saving') : t('creating')) : (editingUser ? t('save') : t('addUser'))}
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => { setShowUserForm(false); setEditingUser(null); }}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              )}

              {loading ? (
                <p className="loading-text">{t('loading')}</p>
              ) : filteredContacts.length === 0 ? (
                <div className="empty-state">
                  <p className="empty-text">{t('noUsers')}</p>
                </div>
              ) : (
                <div className="users-table-wrap">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>{t('name')}</th>
                        <th>Email</th>
                        <th>{t('phone')}</th>
                        <th>{t('role')}</th>
                        <th>{t('lastLogin')}</th>
                        {isAdmin && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredContacts.map((c) => (
                        <tr key={c.id}>
                          <td className="user-name">
                            {c.name}
                            {c.title && <span className="user-title">{c.title}</span>}
                          </td>
                          <td>{c.email || '-'}</td>
                          <td>{c.phone || c.mobile || '-'}</td>
                          <td>
                            <span className={`role-badge role-badge--${c.role}`}>
                              {c.role === 'admin' ? t('admin') : c.role === 'viewer' ? t('viewer') : t('user')}
                            </span>
                          </td>
                          <td className="last-login">{c.last_login ? formatDateTime(c.last_login) : t('never')}</td>
                          {isAdmin && (
                            <td className="user-actions">
                              <button className="btn-edit-user" onClick={() => handleEditUser(c)}>
                                {t('edit')}
                              </button>
                              <button className="btn-delete-user" onClick={() => handleDeleteUser(c)}>
                                {t('delete')}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Administration;
